import * as React from 'react';
import { useEffect, useMemo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'next-i18next';

import { Variable } from '@restworld/utility-types';
import { FormLabelWrapper, Heading, Button } from '@restworld/ui-ds-base';
import { TrashIcon } from '@heroicons/react/24/outline';

import { WizardStepProps } from '../../worker-application-wizard.types';
import { WizardSelectField } from '../wizard-select-field';
import { WizardDateField } from '../wizard-date-field';
import { useErrorMessage } from '../../wizard-application-wizard.utils';
import { useSkippedStep } from '../form-inputs.hooks';
import { useWorkerApplicationCxt } from '../../worker-application-wizard.context';

const rootKey = 'education_em';
const defaultValue = {};
// education_title: '',
// issue_date: '',
export const WizardSeventhStep: React.FunctionComponent<WizardStepProps> = () => {
  const { t } = useTranslation();
  const { control, trigger } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: rootKey,
  });
  const educationField = fields.map((field, fieldIndex) => (
    <React.Fragment key={field.id}>
      <div className="flex justify-start gap-4">
        <Button
          disabled={fields.length === 1}
          onClick={() => remove(fieldIndex)}
          variant="secondary-danger"
          StartIcon={TrashIcon}
        />
        <Heading>{`${t('common:training')} ${fieldIndex + 1}`}</Heading>
      </div>
      <Fields fieldIndex={fieldIndex} />
      {Boolean(fields.length - 1 === fieldIndex) && (
        <div className="self-center">
          <Button
            onClick={async (e) => {
              e?.stopPropagation();
              await trigger(`education_em.${fieldIndex}`)
                .then((r) => {
                  if (r) append(defaultValue, { focusIndex: fieldIndex + 1 });
                })
                .catch((e) => console.error(e, 'error on form validation'));
            }}
            variant="secondary-restworld"
          >
            + {t('onboarding-page:add_training')}
          </Button>
        </div>
      )}
    </React.Fragment>
  ));
  useEffect(() => {
    if (!fields.length) append(defaultValue);
  }, [append, fields.length]);
  return <div className="flex flex-col gap-4 md:h-[26rem] md:overflow-y-auto md:px-1">{educationField}</div>;
};
export default WizardSeventhStep;
const Fields = ({ fieldIndex }: { fieldIndex: number }) => {
  const {
    fieldOptions: { educationTitle },
  } = useWorkerApplicationCxt();
  return (
    <>
      <EducationTitle fieldIndex={fieldIndex} options={educationTitle} />
      <IssueDate fieldIndex={fieldIndex} options={educationTitle} />
    </>
  );
};
const EducationTitle = ({ options, fieldIndex }: { options: Variable[]; fieldIndex: number }) => {
  const { t } = useTranslation();
  const errorMessage = useErrorMessage({
    id: 'education_title',
    fieldIndex,
    rootKey,
  });
  const isSkipped = useSkippedStep();
  return (
    <WizardSelectField
      options={options}
      placeholder={t('onboarding-page:select_the_title')}
      id={`${rootKey}.${fieldIndex}.education_title`}
      formLabel={`${t('onboarding-page:qualification_of_studies')}*`}
      rules={{ required: !isSkipped }}
      errorMessage={errorMessage}
      testId="worker-education-options"
      inputTestId="worker-education-input"
    />
  );
};
const IssueDate = ({ fieldIndex, options }: { fieldIndex: number; options: Variable[] }) => {
  const { t } = useTranslation();
  const errorMessage = useErrorMessage({
    id: 'issue_date',
    fieldIndex,
    rootKey,
  });
  const isSkipped = useSkippedStep();
  const { getValues, setValue } = useFormContext();
  const id = `${rootKey}.${fieldIndex}.issue_date`;
  const educationValue = getValues(`${rootKey}.${fieldIndex}.education_title`);
  const educationKey = useMemo(() => options.find((v) => v.id === educationValue)?.key, [educationValue, options]);
  const isDisabled = useMemo(() => educationKey === 'education_title_nothing', [educationKey]);
  useEffect(() => {
    if (isDisabled) setValue(id, undefined);
  }, [educationKey, id, isDisabled, setValue]);
  return (
    <FormLabelWrapper id={'issue_date'} label={t('common:release_date')}>
      <WizardDateField
        rules={{ required: !isSkipped && !isDisabled }}
        id={`${rootKey}.${fieldIndex}.issue_date`}
        errorMessage={errorMessage}
        disabled={isDisabled}
        testId="issue-date-picker-box"
      />
    </FormLabelWrapper>
  );
};
